/**
 * STICKY ADD TO CART BAR
 *
 * Shows once the real add-to-cart button (referenced via `data-form-id`)
 * scrolls out of view below the viewport, and hides again once it's back
 * in view. Its own button never adds to cart itself -- it just clicks the
 * real submit button, so all existing variant selection, validation
 * (including the custom bag/belt/bundle pickers) and cart-drawer-opening
 * logic keep working unchanged.
 */
import { formatMoney } from 'utils';

class StickyAddToCart extends HTMLElement {
  #observer;
  #variantSelectors = [];
  #boundVariantChange;
  #boundClick;

  connectedCallback() {
    this.formId = this.dataset.formId;
    this.sectionId = this.dataset.sectionId;
    this.button = this.querySelector('[data-sticky-atc-button]');
    this.buttonText = this.querySelector('[data-sticky-atc-button-text]');
    this.priceContainer = this.sectionId ? document.getElementById(`sticky-atc-price-${this.sectionId}`) : null;
    this.image = this.querySelector('.sticky-atc__image');

    this.realForm = this.formId ? document.getElementById(this.formId) : null;
    this.realButton = this.realForm ? this.realForm.querySelector('[name="add"]') : null;

    this.#boundClick = this.#onButtonClick.bind(this);
    this.#boundVariantChange = this.#onVariantChange.bind(this);

    if (this.button) this.button.addEventListener('click', this.#boundClick);

    this.#watchVariantChanges();
    this.#observeRealButton();

    // Only reveal the element to JS-enabled visitors -- it stays `hidden`
    // (and therefore display: none) for everyone else, since it has no
    // useful fallback without the show/hide and click-forwarding logic.
    this.removeAttribute('hidden');
  }

  disconnectedCallback() {
    if (this.#observer) this.#observer.disconnect();
    if (this.button) this.button.removeEventListener('click', this.#boundClick);
    this.#variantSelectors.forEach((el) => el.removeEventListener('on:variant:change', this.#boundVariantChange));
  }

  #observeRealButton() {
    const trigger = this.realButton || this.realForm;
    if (!trigger || !('IntersectionObserver' in window)) return;
    this.#observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.classList.toggle('is-visible', scrolledPast);
        });
      },
      { threshold: 0 }
    );
    this.#observer.observe(trigger);
  }

  #watchVariantChanges() {
    const productSingle = (this.realForm && this.realForm.closest('product-single')) || this.closest('product-single');
    if (!productSingle) return;
    this.#variantSelectors = Array.from(
      productSingle.querySelectorAll('variant-selects, variant-radios, variant-swatches, variant-mixed-inputs')
    );
    this.#variantSelectors.forEach((el) => el.addEventListener('on:variant:change', this.#boundVariantChange));
  }

  #onVariantChange(event) {
    const variant = event.detail && event.detail.variant;
    // The real button can be re-rendered by other components on variant change
    // (e.g. quantity/labels blocks), so re-resolve it rather than trusting a stale reference.
    this.realButton = this.realForm ? this.realForm.querySelector('[name="add"]') : this.realButton;
    if (!variant) {
      this.#setAvailability(false);
      return;
    }
    this.#updatePrice(variant);
    this.#updateImage(variant);
    this.#setAvailability(variant.available);
  }

  #updatePrice(variant) {
    if (!this.priceContainer || !window.theme) return;
    const onSale = variant.compare_at_price > variant.price;
    this.priceContainer.innerHTML = `
      <span class="price__number${onSale ? ' price__number--sale' : ''}">
        <span class="money">${formatMoney(variant.price, window.theme.money_product_price_format)}</span>
      </span>
      ${
        onSale
          ? `<s class="price__compare"><span class="money">${formatMoney(variant.compare_at_price, window.theme.money_format)}</span></s>`
          : ''
      }
    `;
  }

  #updateImage(variant) {
    if (!this.image || !variant.featured_image) return;
    this.image.src = variant.featured_image.src || variant.featured_image.url;
    this.image.srcset = '';
  }

  #setAvailability(available) {
    if (!this.button) return;
    this.button.disabled = !available;
    this.button.classList.toggle('is-disabled', !available);
    if (this.buttonText && window.theme) {
      this.buttonText.textContent = available
        ? window.theme.localize('ADD_TO_CART')
        : window.theme.localize('SOLD_OUT');
    }
  }

  #onButtonClick() {
    this.realButton = this.realForm ? this.realForm.querySelector('[name="add"]') : this.realButton;
    if (!this.realButton || this.realButton.disabled) return;
    this.realButton.click();
  }
}

customElements.define('sticky-add-to-cart', StickyAddToCart);
